import { useGridApiRef } from "@mui/x-data-grid";
import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import useAPI from "../../../../../api/hooks/useAPI";
import { controlKeys } from "../../../../../api/queryKeys";
import LoadingIcon from "../../../../../components/LoadingIcon";
import { generateFileName } from "../../../../../hooks/useFileNaming";
import { canonicalizeEmail } from "../../../../../utils/utils";
import WidgetWrapper from "./../../WidgetWrapper";
import ControlsTableWidget from "./ControlsTableWidget";
import { filterMyTasks } from "./utils/controlFilters";

const MyTasks = () => {
  const { trackEvent, fetchAllControls, getUserEmail } = useAPI();
  const navigate = useNavigate();
  const gridRef = useGridApiRef();
  const { data: controls, isLoading: loadingControls } = useQuery({
    queryKey: controlKeys.all,
    queryFn: fetchAllControls,
    select: (data) =>
      data.data.map((control) => ({
        ...control,
        id: control.control_id,
      })),
  });

  const { data: userEmail } = useQuery({
    queryKey: ["userEmail"],
    queryFn: getUserEmail,
    select: (response) => canonicalizeEmail(response.data),
  });

  const filteredControls = useMemo(() => {
    if (!controls) return [];
    return filterMyTasks(controls, userEmail);
  }, [controls, userEmail]);

  const handleViewAll = () => {
    trackEvent("User clicked on 'View All' button in My Tasks widget");
    navigate("/posture/controls", {
      state: {
        filters: {
          assignee: { [userEmail]: true },
          controlStatuses: {
            Gap: true,
            Compliant: false,
            Excluded: false,
            Snoozed: false,
          },
        },
      },
    });
  };

  const handleTableExport = () => {
    const message =
      "User click on 'Export' button to download the my tasks controls table";
    trackEvent(message);
    const csvExportOptions = {
      fileName: generateFileName(`my-tasks-controls`),
    };
    gridRef?.current?.exportDataAsCsv(csvExportOptions);
  };

  return (
    <WidgetWrapper
      title="My Tasks"
      onExport={handleTableExport}
      info="Gaps assigned to you, sorted by severity"
      viewAll={{
        handleViewAll: handleViewAll,
        tooltip: "Go to the full list view",
        newTab: false,
      }}
    >
      {loadingControls ? (
        <LoadingIcon size={12} />
      ) : (
        <ControlsTableWidget
          controls={filteredControls}
          loading={loadingControls}
          refToPass={gridRef}
          columnsToShow={[
            "id",
            "name",
            "tool",
            "severity",
            "affected_entities_count",
            "assignee",
            "ticket",
          ]}
        />
      )}
    </WidgetWrapper>
  );
};

export default MyTasks;
